import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import Grid from '@mui/material/Grid';
import { useRouter } from 'next/router';
import { sortBy } from 'remeda';
import { useDecision } from '@optimizely/react-sdk';
import ProductId from '../../../common/ProductId';
import useDealMenu from './useDealMenu';
import { onEnteringDealsMenu } from '../analytics';
import { onDealAnalyticsClick } from '@/builders/deals/analytics/analytics';
import CaloriesDisclaimer from '@/common/CaloriesDisclaimer/CaloriesDisclaimer';
import { setDealToDisplay } from '@/builders/deals/slice/dealToDisplay.slice';
import Routes from '@/router/routes';
import CategoryTitle from '@/common/components/CategoryTitle';
import { MenuRecipe } from '@/menu/pizza/pizzaMenuTypes';
import { useApplicationInitialized } from '@/hooks/useApplicationInitialized';
import { useHandleSuccessMenuLoadAfterError } from '@/hooks/useErrorHandling';
import { fromDealsMenu } from '@/builders/deals/slice/userSelections.slice';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import LocalizedMenuSkeleton from '@/menu/deals/LocalizedMenu/LocalizedMenuSkeleton';
import { getOrInitializeOptimizely } from '../../../../optimizely/optimizely';
import isClientSide from '@/common/isClientSide';
import { LINEUP_GLOBALID } from '@/deals/constants';
import { SodiumWarningDisclaimer } from '@/clientCore/components/SodiumWarningDisclaimer/components/SodiumWarningDisclaimer';
import useSodiumAlertForStore from '@/clientCore/components/SodiumWarningDisclaimer/hooks/useSodiumAlertForStore';
import { DealTileList } from '@/clientCore/menu/components/Deal/DealTileList';
import { checkIsPastEndTime, checkIsPastIntervalStartTime, getTimerDetails } from '@/common/countdownTimer/getTimerDetails';
import { openModal } from '@/localization/actions';
import { showDealNotinTimeWindowModal } from '@/common/Modal/modalDetails';
import { localizationSelectors } from '@/localization/localizationSelectors';

const LocalizedMenu = (): JSX.Element | null => {
  const analytics = useAnalytics();
  const appInitialized = useApplicationInitialized();
  const [pageLoadAnalyticsPushed, setPageLoadAnalyticsPushed] = useState(false);
  const occasion = useSelector(localizationSelectors.occasion);
  const storeDetails = useSelector(localizationSelectors.storeDetails);

  const dispatch = useDispatch();
  const router = useRouter();
  const { deals, loading, refetch } = useDealMenu();
  const successMenuLoadAfterRedirectError = useHandleSuccessMenuLoadAfterError();
  const [hideVluDecision] = useDecision('ops-web-1597-hide_vlu_tile', { autoUpdate: true });
  const optimizelyInstance = getOrInitializeOptimizely({ datafile: {}, isBrowser: isClientSide(), allowInitialize: false });
  const [{ enabled: isNewDealTimeExpiresErrorEnabled }] = useDecision('fr-ims-599-fix-dtg-1778');
  const [{ enabled: isStoreTimezoneAvailabilityEnabled }] = useDecision('fr-ims-614-crafted-flatzz-timezone-bug');

  const dealsToDisplay = hideVluDecision.enabled ? deals.filter((deal) => {
    const { globalId } = new ProductId(deal.id);
    return globalId !== LINEUP_GLOBALID;
  }) : deals;

  const { isShowSodiumAlertForStore } = useSodiumAlertForStore();

  useEffect(() => {
    if (appInitialized && !pageLoadAnalyticsPushed && dealsToDisplay.length) {
      const sortedDeals = sortBy(dealsToDisplay,
        (recipe) => recipe.priority,
        (recipe) => recipe.name?.toLowerCase());
      analytics.push(() => onEnteringDealsMenu(sortedDeals));
      setPageLoadAnalyticsPushed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLoadAnalyticsPushed, appInitialized, dealsToDisplay]);

  const onDealClick = (deal: MenuRecipe, index?: number) => {
    const { duration, intervalStartTime } = getTimerDetails(deal.availability, occasion, storeDetails?.storeTimezone);
    const now = isStoreTimezoneAvailabilityEnabled ? new Date(new Date().toLocaleString('en-US', { timeZone: storeDetails?.storeTimezone })) : new Date();
    const isPastIntervalStartTime = checkIsPastIntervalStartTime(now, intervalStartTime);
    const isPastEndtime = Boolean(duration && intervalStartTime && checkIsPastEndTime(storeDetails?.storeTimezone, duration, intervalStartTime));
    if (isNewDealTimeExpiresErrorEnabled && (isPastEndtime || !isPastIntervalStartTime) && refetch) {
      dispatch(openModal(showDealNotinTimeWindowModal(refetch)));
      return;
    }
    const { storeSpecificId } = new ProductId(deal.id);

    const dealId = storeSpecificId;
    analytics.push(() => onDealAnalyticsClick(dealId, deal, index));
    if (dealId.includes(LINEUP_GLOBALID)) {
      optimizelyInstance?.track('ValueLineupClick');
      router.push(Routes.LINEUP);
      return;
    }

    optimizelyInstance?.track('DealsTileClick');

    batch(() => {
      dispatch(fromDealsMenu());
      dispatch(setDealToDisplay({ dealId }));
    });
  };

  if (loading) {
    return <LocalizedMenuSkeleton />;
  }

  /*
   * Here the Deals Menu should have loaded successfully. The router replace is required for the
   * deals back button functionality to work after an error handling has happened.
   */
  successMenuLoadAfterRedirectError(dealsToDisplay.length > 0, () => {
    router.replace(Routes.DEALS, undefined, { shallow: true });
  });

  return (
    <Grid data-testid="deal-menu-page-localized">
      <CategoryTitle title="Deals" />
          <DealTileList
              key='deals-deal-tile-list'
              deals={dealsToDisplay}
              onDealClick={onDealClick} 
              isLoading={loading}
          />
      <CaloriesDisclaimer />
      {isShowSodiumAlertForStore && <SodiumWarningDisclaimer />}
    </Grid>
  );
};

export default LocalizedMenu;
